import React, { useState, useEffect } from 'react';
import { Save, Settings, RefreshCw, ShieldCheck } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useToast } from '../../contexts/ToastContext';
import { openaiService, AISettings } from '../../services/openaiService';

const OpenAIConfiguration: React.FC = () => {
  const { addToast } = useToast();
  const [settings, setSettings] = useState<AISettings>(openaiService.getSettings());
  const [isSaving, setIsSaving] = useState(false);

  // Load settings on component mount
  useEffect(() => {
    setSettings(openaiService.getSettings());
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      openaiService.updateSettings(settings);
      addToast('OpenAI configuration saved successfully', 'success');
    } catch (error) {
      addToast('Failed to save configuration', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefaults = () => {
    const defaultSettings: AISettings = {
      model: 'gpt-4o-mini',
      temperature: 0.3,
      maxTokens: 4000,
      systemPrompt: `You are an expert timetabling assistant for a secondary school. Your role is to analyze teacher qualifications, preferences, workloads, and constraints to suggest optimal class assignments.

Key considerations:
1. Teacher qualifications and subject expertise
2. Teaching preferences and intentions
3. Workload distribution and FTE considerations
4. Avoiding conflicts and overloading
5. Curriculum area expertise
6. Year level preferences
7. Professional development goals

Always provide specific reasoning for your suggestions and flag any potential conflicts.`,

      analysisPrompt: `Analyze the current timetabling situation and provide insights:

1. Workload Distribution Analysis
2. Preference Satisfaction Assessment
3. Qualification Matching Review
4. Potential Conflicts and Issues
5. Recommendations for Improvement

Focus on actionable insights that help administrators make better decisions.`,

      assignmentPrompt: `Generate teacher assignments for the unassigned classes, considering:

1. Match teachers to their qualified subjects
2. Respect teaching preferences where possible
3. Balance workloads according to FTE
4. Avoid scheduling conflicts
5. Consider professional development opportunities
6. Maintain curriculum area expertise

Provide confidence scores (0-100) and detailed reasoning for each assignment.`
    };
    setSettings(defaultSettings);
    addToast('Settings reset to defaults', 'info');
  };

  return (
    <div>
      <PageHeader
        title="OpenAI Configuration"
        description="Configure OpenAI integration for AI-powered timetabling suggestions"
        actions={
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={resetToDefaults}
              leftIcon={<RefreshCw className="h-4 w-4" />}
            >
              Reset Defaults
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={isSaving}
              leftIcon={<Save className="h-4 w-4" />}
            >
              Save Configuration
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {/* API Key location notice */}
        <Card>
          <div className="p-6">
            <div className="flex items-start">
              <ShieldCheck className="h-5 w-5 text-ok-fg mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-card-title text-ink-900 mb-1">API Key</h3>
                <p className="text-sm text-ink-500">
                  The OpenAI API key is stored securely on the server and is
                  never exposed to the browser. AI requests are proxied through
                  a secure server-side function, so there is no API key to enter
                  here.
                </p>
                <p className="mt-2 text-sm text-ink-500">
                  To set or rotate the key, an administrator updates the{' '}
                  <code className="px-1 py-0.5 bg-ink-100 rounded text-xs">OPENAI_API_KEY</code>{' '}
                  secret on the <code className="px-1 py-0.5 bg-ink-100 rounded text-xs">openai-suggest</code>{' '}
                  Supabase Edge Function.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Model Settings */}
        <Card>
          <div className="p-6">
            <div className="flex items-center mb-4">
              <Settings className="h-5 w-5 text-navy-600 mr-2" />
              <h3 className="text-card-title text-ink-900">Model Settings</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Model
                </label>
                <select
                  value={settings.model}
                  onChange={(e) => setSettings({ ...settings, model: e.target.value })}
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                >
                  <option value="gpt-4o-mini">GPT-4o Mini (Recommended)</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Temperature ({settings.temperature})
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={settings.temperature}
                  onChange={(e) => setSettings({ ...settings, temperature: parseFloat(e.target.value) })}
                  className="block w-full"
                />
                <div className="flex justify-between text-xs text-ink-500 mt-1">
                  <span>Focused</span>
                  <span>Creative</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Max Tokens
                </label>
                <input
                  type="number"
                  min="500"
                  max="8000"
                  value={settings.maxTokens}
                  onChange={(e) => setSettings({ ...settings, maxTokens: parseInt(e.target.value) })}
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Prompt Configuration */}
        <Card>
          <div className="p-6">
            <h3 className="text-card-title text-ink-900 mb-4">Custom Prompts</h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  System Prompt
                </label>
                <textarea
                  value={settings.systemPrompt}
                  onChange={(e) => setSettings({ ...settings, systemPrompt: e.target.value })}
                  rows={8}
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm font-mono text-sm"
                  placeholder="Define the AI's role and key considerations..."
                />
                <p className="mt-1 text-sm text-ink-500">
                  Sets the AI's role and overall behavior for timetabling tasks.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Analysis Prompt
                </label>
                <textarea
                  value={settings.analysisPrompt}
                  onChange={(e) => setSettings({ ...settings, analysisPrompt: e.target.value })}
                  rows={6}
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm font-mono text-sm"
                  placeholder="Define how the AI should analyze timetabling situations..."
                />
                <p className="mt-1 text-sm text-ink-500">
                  Instructions for generating insights and observations about the current timetable.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Assignment Prompt
                </label>
                <textarea
                  value={settings.assignmentPrompt}
                  onChange={(e) => setSettings({ ...settings, assignmentPrompt: e.target.value })}
                  rows={6}
                  className="block w-full border border-ink-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm font-mono text-sm"
                  placeholder="Define how the AI should generate teacher assignments..."
                />
                <p className="mt-1 text-sm text-ink-500">
                  Instructions for generating specific teacher-to-class assignments.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Usage Guidelines */}
        <Card>
          <div className="p-6">
            <h3 className="text-card-title text-ink-900 mb-4">Usage Guidelines</h3>
            <div className="prose text-sm text-ink-500">
              <ul>
                <li><strong>API Costs:</strong> OpenAI charges per token used. Monitor your usage in the OpenAI dashboard.</li>
                <li><strong>Data Privacy:</strong> Your timetabling data will be sent to OpenAI for processing.</li>
                <li><strong>Model Selection:</strong> GPT-4o Mini offers the best balance of cost and performance for timetabling.</li>
                <li><strong>Temperature:</strong> Lower values (0.1-0.3) for consistent results, higher for more creative suggestions.</li>
                <li><strong>Custom Prompts:</strong> Tailor the AI's behavior to your school's specific requirements and policies.</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OpenAIConfiguration;
